import React, { useCallback, useRef, useState } from 'react';

// Interactive before/after comparison slider.
// Mouse + touch (Pointer Events) + keyboard (Left/Right/Home/End). Focusable handle.
const BeforeAfterSlider = ({ before, after }) => {
  const [pct, setPct] = useState(50);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
    setPct(p);
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  };
  const onKey = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); setPct((p) => Math.max(0, p - 5)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); setPct((p) => Math.min(100, p + 5)); }
    else if (e.key === 'Home') { e.preventDefault(); setPct(0); }
    else if (e.key === 'End') { e.preventDefault(); setPct(100); }
  };

  return (
    <div
      ref={containerRef}
      className="ba-slider"
      data-testid="before-after-slider"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* After = base layer */}
      <picture>
        <source type="image/webp" srcSet={after.webp} />
        <img src={after.jpg} alt={after.alt} className="ba-img" draggable="false" />
      </picture>
      {/* Before = top layer, clipped by pct */}
      <div className="ba-clip" style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }} aria-hidden="true">
        <picture>
          <source type="image/webp" srcSet={before.webp} />
          <img src={before.jpg} alt="" className="ba-img" draggable="false" />
        </picture>
      </div>

      <span className="ba-tag ba-tag-before" aria-hidden="true">Before</span>
      <span className="ba-tag ba-tag-after" aria-hidden="true">After</span>

      <div
        className="ba-handle"
        role="slider"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={Math.round(pct)}
        aria-label="Before / After comparison — drag or use left and right arrow keys"
        tabIndex="0"
        onKeyDown={onKey}
        style={{ left: `${pct}%` }}
        data-testid="ba-handle"
      >
        <span className="ba-handle-line" aria-hidden="true" />
        <span className="ba-handle-knob" aria-hidden="true">
          <span>‹</span><span>›</span>
        </span>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
